/**
 * Approved content.
 *
 * Centrally managed standard wording, grouped by category. Editing the wording
 * writes a new version and leaves the old one intact, because a proposal
 * generated last quarter records which version it used and that record has to
 * stay resolvable.
 */
import { useEffect, useState } from 'react';
import {
  CONTENT_CATEGORY_LABELS,
  endpoints,
  type ApprovedContent,
  type ApprovedContentCategory,
  type ApprovedContentVersion,
} from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Select,
  Skeleton,
  formatDateTime,
} from '../../components/ui';
import { ControlledBadge, Empty, StatusPill } from './shared';

export function ContentAdmin({ onError }: { onError(message: string): void }) {
  const [content, setContent] = useState<ApprovedContent[] | null>(null);
  const [categories, setCategories] = useState<ApprovedContentCategory[]>([]);
  const [controlled, setControlled] = useState<ApprovedContentCategory[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<'ALL' | ApprovedContentCategory>('ALL');

  const load = () =>
    endpoints
      .approvedContent()
      .then((result) => {
        setContent(result.content);
        setCategories(result.categories);
        setControlled(result.controlledCategories);
      })
      .catch((caught) => onError(String(caught)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      await load();
      setNotice(message);
      setEditing(null);
      setCreating(false);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  if (!content) return <Skeleton className="h-64 w-full" />;

  const shown = filter === 'ALL' ? content : content.filter((c) => c.category === filter);
  const byCategory = new Map<ApprovedContentCategory, ApprovedContent[]>();
  for (const entry of shown) {
    const list = byCategory.get(entry.category) ?? [];
    list.push(entry);
    byCategory.set(entry.category, list);
  }

  return (
    <Card
      title="Approved content"
      description="Standard wording held centrally, so a change is an edit rather than a deploy."
      actions={
        <div className="flex items-center gap-2">
          <Select
            aria-label="Filter by category"
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
          >
            <option value="ALL">All categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {CONTENT_CATEGORY_LABELS[category]}
              </option>
            ))}
          </Select>
          <Button size="sm" onClick={() => setCreating(true)} disabled={creating}>
            Add content
          </Button>
        </div>
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      <div className="mb-4 rounded-md border border-rule bg-tint px-4 py-3 text-[12px] text-muted">
        <strong className="text-ink">Corporate claims are controlled.</strong>{' '}
        {controlled.map((c) => CONTENT_CATEGORY_LABELS[c]).join(' and ')} hold quantitative
        claims about the company. Only an administrator may write them — neither Sales nor an AI
        assistant can originate or alter them, and the rule is enforced on the write path, not
        just by hiding a field.
      </div>

      {creating && (
        <ContentForm
          categories={categories}
          controlled={controlled}
          busy={busy}
          onCancel={() => setCreating(false)}
          onSubmit={(values) =>
            void run(
              () => endpoints.createApprovedContent(values),
              `"${values.title}" added at version 1.`,
            )
          }
        />
      )}

      {shown.length === 0 ? (
        <Empty>No approved content in this category yet.</Empty>
      ) : (
        [...byCategory.entries()].map(([category, entries]) => (
          <section key={category} className="mt-5 first:mt-0">
            <h3 className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-muted">
              {CONTENT_CATEGORY_LABELS[category]}
              {controlled.includes(category) && <ControlledBadge />}
            </h3>
            <ul className="mt-2 space-y-3">
              {entries.map((entry) => (
                <ContentRow
                  key={entry.contentId}
                  entry={entry}
                  categories={categories}
                  controlled={controlled}
                  busy={busy}
                  editing={editing === entry.contentId}
                  onEdit={() => setEditing(entry.contentId)}
                  onCancel={() => setEditing(null)}
                  onSave={(values) =>
                    void run(
                      () => endpoints.updateApprovedContent(entry.contentId, values),
                      `"${values.title ?? entry.title}" saved.`,
                    )
                  }
                  onToggleStatus={() =>
                    void run(
                      () =>
                        endpoints.setApprovedContentStatus(
                          entry.contentId,
                          entry.status === 'ACTIVE' ? 'RETIRED' : 'ACTIVE',
                        ),
                      entry.status === 'ACTIVE'
                        ? `"${entry.title}" retired.`
                        : `"${entry.title}" restored.`,
                    )
                  }
                  onError={onError}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </Card>
  );
}

function ContentRow({
  entry,
  categories,
  controlled,
  busy,
  editing,
  onEdit,
  onCancel,
  onSave,
  onToggleStatus,
  onError,
}: {
  entry: ApprovedContent;
  categories: ApprovedContentCategory[];
  controlled: ApprovedContentCategory[];
  busy: boolean;
  editing: boolean;
  onEdit(): void;
  onCancel(): void;
  onSave(values: {
    title: string;
    category: ApprovedContentCategory;
    content: string;
    note?: string;
  }): void;
  onToggleStatus(): void;
  onError(message: string): void;
}) {
  const [history, setHistory] = useState<ApprovedContentVersion[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const toggleHistory = async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    if (history) return;
    try {
      const result = await endpoints.approvedContentVersions(entry.contentId);
      setHistory(result.versions);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  return (
    <li className="rounded-md border border-rule px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="flex items-center gap-2 text-[13px] font-semibold text-ink">
          {entry.title}
          <span className="text-[11px] font-normal tabular-nums text-muted">
            v{entry.version}
          </span>
          <StatusPill status={entry.status} />
          {entry.controlled && <ControlledBadge />}
        </p>
        <p className="text-[12px] text-muted">
          updated {formatDateTime(entry.updatedAt)}
          {entry.updatedByUserId ? ` · ${entry.updatedByUserId}` : ''}
        </p>
      </div>

      {editing ? (
        <ContentForm
          initial={entry}
          categories={categories}
          controlled={controlled}
          busy={busy}
          onCancel={onCancel}
          onSubmit={onSave}
        />
      ) : (
        <>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-body">
            {entry.content}
          </pre>
          <div className="mt-2 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onEdit}
              className="text-[12px] font-semibold text-teal underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onToggleStatus}
              disabled={busy}
              className="text-[12px] font-semibold text-muted underline disabled:opacity-50"
            >
              {entry.status === 'ACTIVE' ? 'Retire' : 'Restore'}
            </button>
            <button
              type="button"
              onClick={() => void toggleHistory()}
              className="text-[12px] font-semibold text-muted underline"
            >
              {showHistory ? 'Hide history' : `History (${entry.version})`}
            </button>
          </div>
        </>
      )}

      {showHistory && (
        <div className="mt-3 border-t border-rule pt-3">
          {!history ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <ul className="space-y-2">
              {history.map((version) => (
                <li key={version.version} className="text-[12px]">
                  <span className="font-semibold tabular-nums text-ink">v{version.version}</span>{' '}
                  <span className="text-muted">
                    {formatDateTime(version.createdAt)} · {version.note}
                  </span>
                  <pre className="mt-1 whitespace-pre-wrap font-sans text-[12px] text-muted">
                    {version.content}
                  </pre>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function ContentForm({
  initial,
  categories,
  controlled,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: ApprovedContent;
  categories: ApprovedContentCategory[];
  controlled: ApprovedContentCategory[];
  busy: boolean;
  onCancel(): void;
  onSubmit(values: {
    title: string;
    category: ApprovedContentCategory;
    content: string;
    note?: string;
  }): void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [category, setCategory] = useState<ApprovedContentCategory>(
    initial?.category ?? 'OTHER',
  );
  const [body, setBody] = useState(initial?.content ?? '');
  const [note, setNote] = useState('');

  const wordingChanged = !initial || body !== initial.content || category !== initial.category;

  return (
    <form
      className="mt-3 space-y-3 rounded-md bg-tint px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ title, category, content: body, note: note || undefined });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Title" htmlFor="content-title" required>
          <Input
            id="content-title"
            value={title}
            required
            maxLength={160}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field label="Category" htmlFor="content-category" required>
          <Select
            id="content-category"
            value={category}
            onChange={(event) => setCategory(event.target.value as ApprovedContentCategory)}
          >
            {categories.map((option) => (
              <option key={option} value={option}>
                {CONTENT_CATEGORY_LABELS[option]}
                {controlled.includes(option) ? ' (controlled)' : ''}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field
        label="Content"
        htmlFor="content-body"
        required
        hint="One item per line where the template renders a list."
      >
        <textarea
          id="content-body"
          value={body}
          required
          rows={6}
          maxLength={20000}
          onChange={(event) => setBody(event.target.value)}
          className="w-full rounded-md border border-rule bg-white px-3 py-2 text-[13px] leading-relaxed text-ink outline-none focus:border-teal"
        />
      </Field>

      <Field
        label="Change note"
        htmlFor="content-note"
        hint={
          wordingChanged
            ? 'Saved with the new version, so the history explains itself later.'
            : 'Only the title or scope changed, so no new version is written.'
        }
      >
        <Input
          id="content-note"
          value={note}
          maxLength={200}
          placeholder="e.g. Updated retention figure for FY26"
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>

      <div className="flex gap-3">
        <Button type="submit" size="sm" loading={busy}>
          {initial ? 'Save' : 'Add content'}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
